import { HttpContext } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import { Observable, switchMap } from 'rxjs';

import { AuthService, TenantService } from '@celum/authentication';
import { CelumPropertiesProvider } from '@celum/core';
import { BASIC_AUTH_CONTEXT } from '@celum/shared/shared';

import { AdminService } from './admin/admin.service';

/**
 * Identifies the app/embed context a `FormatsContextAware` subclass is running in, so it can resolve the correct
 * tenantId and authentication strategy for outgoing HTTP calls without every concrete service having to reimplement it.
 */
export type FormatsContext = 'FORMATS_APP' | 'EMBED_MY_PROFILE' | 'EMBED_ORGANIZATION_ADMINISTRATION' | 'EMBED_GLOBAL_ADMINISTRATION';

export const FORMATS_CONTEXT = new InjectionToken<FormatsContext>('FORMATS_CONTEXT');

/**
 * Base class for services calling the formats API from either the standalone `formats` app or the `formats-embed`
 * app. Provides the tenant/apiUrl resolution and the `withContext` helper that attaches the `BASIC_AUTH_CONTEXT`
 * (bearer token) required by the `SaccAuthInterceptor` when running embedded, so concrete services only need to
 * implement their actual API calls once. This class is intentionally generic (not tied to any single domain
 * service contract) so it can be reused by any formats-related service (e.g. `FormatsService`, `SpecificationsService`).
 */
export abstract class FormatsContextAware {
  protected readonly context = inject(FORMATS_CONTEXT);
  private readonly tenantService = inject(TenantService);
  private readonly authService = inject(AuthService);
  private readonly adminService = inject(AdminService);

  public isEmbed(): boolean {
    return this.context === 'EMBED_MY_PROFILE' || this.context === 'EMBED_GLOBAL_ADMINISTRATION' || this.context === 'EMBED_ORGANIZATION_ADMINISTRATION';
  }

  protected get apiUrl(): string {
    const tenant = this.getTenantId();
    return this.adminService.isAdmin()
      ? `${CelumPropertiesProvider.properties.appProperties.formats.apiUrl}/configurations/${tenant}`
      : CelumPropertiesProvider.properties.appProperties.formats.apiUrl;
  }

  protected getTenantId(): string {
    switch (this.context) {
      case 'EMBED_ORGANIZATION_ADMINISTRATION':
        return this.tenantService.getTenantIdFromUrl('accounts');
      case 'EMBED_MY_PROFILE':
        return this.tenantService.getCurrentTenantId({ fallbackToUrl: true, tenantIdPrefix: 'formats' });
      case 'EMBED_GLOBAL_ADMINISTRATION':
        // this route has no accountId (it's not scoped to a single organization); the interceptor still requires a
        // non-empty tenantId to attach the Authorization header, so a placeholder is used here (only the bearer
        // token matters for these calls).
        return 'formats-embed';
      case 'FORMATS_APP':
        return this.tenantService.getCurrentTenantId();
      default:
        throw new Error(`FormatsContextAware: unknown context "${this.context}"`);
    }
  }

  protected withContext<T>(requestFactory: (context?: HttpContext) => Observable<T>, extraContext?: HttpContext): Observable<T> {
    if (this.context === 'FORMATS_APP') {
      return requestFactory(extraContext);
    }

    return this.authService
      .getB2CToken()
      .pipe(switchMap(token => requestFactory((extraContext ?? new HttpContext()).set(BASIC_AUTH_CONTEXT, { token, tenantId: this.getTenantId() }))));
  }
}
